/**
 * Sessions Page - Rewritten with Real-Time WebSocket Integration
 * View and analyze user sessions with live updates
 */

import { useState, useEffect, useCallback } from 'react';
import * as React from 'react';
import {
  Users,
  Monitor,
  Smartphone,
  Tablet,
  Clock,
  ExternalLink,
  RefreshCw,
  Filter,
  Globe,
  FileText,
  ChevronLeft,
  ChevronRight,
  Eye,
  AlertTriangle,
  XCircle,
  Star,
  Zap,
  ArrowUpDown,
  LogOut,
  TrendingUp,
  Activity,
  DollarSign,
  Flame,
} from 'lucide-react';
import { api } from '../../services/api';
import { useDataSourceStore } from '@store/dataSourceStore';
import { useWebSocket } from '@hooks/useWebSocket';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import SessionJourneyModal from '@components/sessions/SessionJourneyModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

interface SessionData {
  session_id: string;
  date: string;
  first_timestamp: string;
  last_timestamp: string;
  total_pages: number;
  duration_seconds: number;
  first_page: string;
  last_page: string;
  first_page_title: string;
  last_page_title: string;
  device_type: string;
  browser: string;
  os: string;
  utm_source: string;
  utm_campaign?: string;
  utm_medium?: string;
  user_id?: string;
  quality: {
    isBounce: boolean;
    isShortSession: boolean;
    isLongSession: boolean;
    hasLowEngagement: boolean;
    hasHighEngagement: boolean;
  };
  pages: Array<{
    url: string;
    title: string;
    timestamp: string;
  }>;
  // 🎯 NEW: User information extracted from page URLs
  userInfo?: {
    userId: number;
    name: string | null;
    email: string | null;
    phoneNumber: string | null;
    totalOrders?: number;
    lastOrderDate?: Date;
    isUnknownUser?: boolean;
    multipleUserIds?: number[];
    multipleUsersDetected?: boolean;
    // 💰 Rich Customer Analytics
    customerMetrics?: {
      daysSinceJoined: number;
      daysSinceLastOrder: number | null;
      isNewCustomer: boolean;
      isReturningCustomer: boolean;
      isVIPCustomer: boolean;
      isAtRiskCustomer: boolean;
      customerSegment: string;
      estimatedLifetimeValue: number;
      avgOrderValue: number;
      monthlyOrderFrequency: number;
      customerAge: string;
      totalSpent: number;
    };
  };
}

interface SessionsResponse {
  sessions: SessionData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const SessionsPage = () => {
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();

  // Filter to only use data sources that support 'sessions' module
  const sessionsDataSources = selectedDataSources.filter(ds =>
    (ds.availableModules || []).includes('sessions')
  );
  const selectedDataSource = sessionsDataSources[0] || null;

  const { companyId } = getSelection();
  const dataSourceId = selectedDataSource?.id || null;
  const { socket, connected } = useWebSocket({ autoConnect: true });
  const queryClient = useQueryClient();

  // 🐛 DEBUG: Log WebSocket connection status
  useEffect(() => {
    console.log('🔍 [SessionsPage] WebSocket status:', {
      socket: !!socket,
      connected,
      companyId,
      dataSourceId,
    });
  }, [socket, connected, companyId, dataSourceId]);

  // Date range state (default: Last 7 days)
  const getDefaultDateRange = (): DateRange => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 7 days',
    };
  };

  // Load filters from localStorage or use defaults
  const loadFiltersFromStorage = () => {
    try {
      const stored = localStorage.getItem('sessions-filters');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          dateRange: parsed.dateRange || getDefaultDateRange(),
          deviceFilter: parsed.deviceFilter || 'all',
          utmSourceFilter: parsed.utmSourceFilter || '',
          utmCampaignFilter: parsed.utmCampaignFilter || '',
          utmMediumFilter: parsed.utmMediumFilter || '',
          currentPageFilter: parsed.currentPageFilter || { operator: 'contains', value: '' },
        };
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return {
      dateRange: getDefaultDateRange(),
      deviceFilter: 'all',
      utmSourceFilter: '',
      utmCampaignFilter: '',
      utmMediumFilter: '',
      currentPageFilter: { operator: 'contains', value: '' },
    };
  };

  const savedFilters = loadFiltersFromStorage();

  const [dateRange, setDateRange] = useState<DateRange>(savedFilters.dateRange);
  const [deviceFilter, setDeviceFilter] = useState<string>(savedFilters.deviceFilter);
  const [utmSourceFilter, setUtmSourceFilter] = useState<string>(savedFilters.utmSourceFilter);
  const [utmCampaignFilter, setUtmCampaignFilter] = useState<string>(
    savedFilters.utmCampaignFilter
  );
  const [utmMediumFilter, setUtmMediumFilter] = useState<string>(savedFilters.utmMediumFilter);
  const [currentPageFilter, setCurrentPageFilter] = useState<{
    operator: 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'notContains';
    value: string;
  }>(savedFilters.currentPageFilter);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [newSessions, setNewSessions] = useState<SessionData[]>([]);
  const [sortBy, setSortBy] = useState<string>('last_timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [clientSortBy, setClientSortBy] = useState<
    'session_id' | 'first_timestamp' | 'last_timestamp' | 'total_pages' | 'duration_seconds'
  >('last_timestamp');
  const [clientSortOrder, setClientSortOrder] = useState<'asc' | 'desc'>('desc');
  const limit = 20;

  // Autocomplete states for current page filter
  const [currentPageSearch, setCurrentPageSearch] = useState('');
  const [showCurrentPageDropdown, setShowCurrentPageDropdown] = useState(false);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        'sessions-filters',
        JSON.stringify({
          dateRange,
          deviceFilter,
          utmSourceFilter,
          utmCampaignFilter,
          utmMediumFilter,
          currentPageFilter,
        })
      );
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [
    dateRange,
    deviceFilter,
    utmSourceFilter,
    utmCampaignFilter,
    utmMediumFilter,
    currentPageFilter,
  ]);

  // Fetch sessions
  const {
    data: sessionsResponse,
    isLoading,
    refetch,
  } = useQuery<{ data: SessionsResponse }>({
    queryKey: [
      'sessions',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
      deviceFilter,
      utmSourceFilter,
      utmCampaignFilter,
      utmMediumFilter,
      currentPageFilter,
      sortBy,
      sortOrder,
      page,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) {
        throw new Error('Please select a company and data source');
      }

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page,
        limit,
        sortBy,
        sortOrder,
      };

      if (deviceFilter !== 'all') params.deviceType = deviceFilter;
      if (utmSourceFilter) params.utmSource = utmSourceFilter;
      if (utmCampaignFilter) params.utmCampaign = utmCampaignFilter;
      if (utmMediumFilter) params.utmMedium = utmMediumFilter;

      // Add current page filter
      if (currentPageFilter.value.trim()) {
        params.currentPage = currentPageFilter.value.trim();
        params.currentPageOperator = currentPageFilter.operator;
        console.log('🔍 Current page filter applied:', {
          operator: currentPageFilter.operator,
          value: currentPageFilter.value,
          trimmedValue: currentPageFilter.value.trim(),
          params,
        });
      } else {
        console.log('⚠️ Current page filter NOT applied - value is empty');
      }

      console.log('📤 Sending request with params:', params);
      const response = await api.get('/api/v1/analytics/sessions', { params });
      console.log('📊 Sessions response:', {
        success: response.data.success,
        total: response.data.data?.total,
        sessions: response.data.data?.sessions?.length,
        firstSession: response.data.data?.sessions?.[0],
      });
      return response.data;
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 30000,
  });

  // Fetch UTM sources for autocomplete
  const { data: utmSources } = useQuery({
    queryKey: ['utm-sources-sessions', companyId, dataSourceId, dateRange],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/sessions/utm-sources', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId && showFilters,
    staleTime: 300000, // 5 min
  });

  // Fetch UTM campaigns for autocomplete (cascading)
  const { data: utmCampaigns } = useQuery({
    queryKey: ['utm-campaigns-sessions', companyId, dataSourceId, dateRange, utmSourceFilter],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/sessions/utm-campaigns', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          utmSource: utmSourceFilter || undefined,
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId && showFilters,
    staleTime: 300000,
  });

  // Fetch UTM mediums for autocomplete (cascading)
  const { data: utmMediums } = useQuery({
    queryKey: ['utm-mediums-sessions', companyId, dataSourceId, dateRange, utmSourceFilter],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/sessions/utm-mediums', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          utmSource: utmSourceFilter || undefined,
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId && showFilters,
    staleTime: 300000,
  });

  // Fetch available pages for autocomplete
  const { data: availablePages } = useQuery({
    queryKey: ['available-pages-sessions', companyId, dataSourceId, dateRange],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/sessions/pages', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          // Don't send search param - fetch all pages and filter client-side
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId && showFilters,
    staleTime: 300000,
  });

  const rawSessions = sessionsResponse?.data?.sessions || [];
  const apiTotal = sessionsResponse?.data?.total || 0;
  const apiTotalPages = sessionsResponse?.data?.totalPages || 0;

  // Client-side sorting for instant response (includes new sessions from WebSocket)
  const sessions = React.useMemo(() => {
    // Combine new sessions with fetched sessions
    const allSessions = [...newSessions, ...rawSessions];

    const sorted = allSessions.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (clientSortBy) {
        case 'session_id':
          aVal = a.session_id || '';
          bVal = b.session_id || '';
          break;
        case 'first_timestamp':
          aVal = new Date(a.first_timestamp).getTime();
          bVal = new Date(b.first_timestamp).getTime();
          break;
        case 'last_timestamp':
          aVal = new Date(a.last_timestamp).getTime();
          bVal = new Date(b.last_timestamp).getTime();
          break;
        case 'total_pages':
          aVal = a.total_pages || 0;
          bVal = b.total_pages || 0;
          break;
        case 'duration_seconds':
          aVal = a.duration_seconds || 0;
          bVal = b.duration_seconds || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return clientSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return clientSortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [rawSessions, newSessions, clientSortBy, clientSortOrder]);

  const totalSessions = apiTotal + newSessions.length;
  const totalPages = Math.ceil(totalSessions / limit);

  const { data: statsResponse } = useQuery({
    queryKey: [
      'sessions-stats',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
      deviceFilter,
      utmSourceFilter,
      utmCampaignFilter,
      utmMediumFilter,
      currentPageFilter.value,
      currentPageFilter.operator,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return null;

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      // Add filters to stats query (same as sessions query)
      if (deviceFilter && deviceFilter !== 'all') params.deviceType = deviceFilter;
      if (utmSourceFilter) params.utmSource = utmSourceFilter;
      if (utmCampaignFilter) params.utmCampaign = utmCampaignFilter;
      if (utmMediumFilter) params.utmMedium = utmMediumFilter;
      if (currentPageFilter.value) {
        params.currentPage = currentPageFilter.value;
        params.currentPageOperator = currentPageFilter.operator;
      }

      const response = await api.get('/api/v1/analytics/sessions/stats', { params });
      return response.data;
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 60000,
  });

  const stats = statsResponse?.data || {
    total_sessions: 0,
    avg_pages_per_session: 0,
    avg_duration_seconds: 0,
    desktop_sessions: 0,
    mobile_sessions: 0,
    tablet_sessions: 0,
    bounce_rate: 0,
    high_engagement_rate: 0,
    paid_traffic_rate: 0,
    active_sessions: 0,
    long_sessions: 0,
  };

  // WebSocket: Subscribe to real-time session updates
  useEffect(() => {
    console.log('🔍 WebSocket Effect:', {
      socket: !!socket,
      connected,
      companyId,
      dataSourceId,
    });

    if (!socket || !connected || !companyId || !dataSourceId) {
      console.warn('⚠️  Cannot subscribe to sessions:', {
        hasSocket: !!socket,
        connected,
        hasCompanyId: !!companyId,
        hasDataSourceId: !!dataSourceId,
      });
      return;
    }

    // Subscribe to sessions room
    console.log(`📡 Subscribing to sessions room: sessions:${companyId}:${dataSourceId}`);
    socket.emit('sessions:subscribe', { companyId, dataSourceId });

    // Listen for new sessions
    const handleNewSession = (data: { session: SessionData; timestamp: string }) => {
      console.log('📊 New session received:', data);

      // Add to new sessions list (will be prepended to table)
      setNewSessions(prev => {
        // Check if session already exists
        const exists = prev.some(s => s.session_id === data.session.session_id);
        if (exists) return prev;

        // Add new session to top, limit to 10 new sessions
        const updated = [data.session, ...prev].slice(0, 10);
        return updated;
      });

      // Show toast notification
      toast.success(`New session started! Device: ${data.session.device_type}`, {
        duration: 3000,
        icon: '🔥',
      });

      // Update stats cache directly for instant update - use the latest query key
      const statsQueries = queryClient
        .getQueryCache()
        .findAll(['sessions-stats', companyId, dataSourceId]);

      statsQueries.forEach(query => {
        queryClient.setQueryData(query.queryKey, (oldData: any) => {
          if (!oldData?.data) return oldData;

          const stats = oldData.data;
          const isDesktop = data.session.device_type === 'desktop';

          return {
            ...oldData,
            data: {
              ...stats,
              total_sessions: stats.total_sessions + 1,
              desktop_sessions: isDesktop ? stats.desktop_sessions + 1 : stats.desktop_sessions,
              mobile_sessions: isDesktop ? stats.mobile_sessions : stats.mobile_sessions + 1,
              // Recalculate average pages per session
              avg_pages_per_session:
                (stats.avg_pages_per_session * stats.total_sessions + data.session.total_pages) /
                (stats.total_sessions + 1),
              // Recalculate average duration
              avg_duration_seconds:
                (stats.avg_duration_seconds * stats.total_sessions +
                  data.session.duration_seconds) /
                (stats.total_sessions + 1),
            },
          };
        });
      });
    };

    // Listen for session updates
    const handleSessionUpdate = (data: { session: SessionData; timestamp: string }) => {
      console.log('📊 Session updated:', data);

      // Update existing session in new sessions list (let sorting handle position)
      setNewSessions(prev => {
        const existingIndex = prev.findIndex(s => s.session_id === data.session.session_id);
        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          const mergedSession = {
            ...existing, // Keep existing data (including userInfo)
            ...data.session, // Update with new data
            userInfo: existing.userInfo || data.session.userInfo, // Preserve userInfo
          };
          
          console.log('🔄 Updating session data - will auto-resort:', data.session.session_id);
          
          // Just update in place - the useMemo sorting will handle positioning
          const updated = [...prev];
          updated[existingIndex] = mergedSession;
          return updated;
        }
        return prev;
      });

      // Update the session in React Query cache for ALL pages
      // We need to update all query cache entries, not just the current page
      const queries = queryClient.getQueryCache().findAll(['sessions', companyId, dataSourceId]);

      let oldSession: SessionData | null = null;

      queries.forEach(query => {
        queryClient.setQueryData<{ data: SessionsResponse }>(query.queryKey, oldData => {
          if (!oldData?.data?.sessions) return oldData;

          let oldSession: SessionData | null = null;
          let sessionIndex = -1;

          // Find the session to update
          const existingSession = oldData.data.sessions.find((s, idx) => {
            if (s.session_id === data.session.session_id) {
              oldSession = s;
              sessionIndex = idx;
              return true;
            }
            return false;
          });

          if (!existingSession) return oldData;

          // Merge the updated session
          const mergedSession = {
            ...existingSession, // Keep existing data (including userInfo)
            ...data.session, // Update with new data (pages, duration, etc.)
            userInfo: existingSession.userInfo || data.session.userInfo, // Preserve userInfo
          };

          console.log('🔄 Updating session in cache - will auto-resort:', data.session.session_id);

          // Just update in place - the useMemo sorting will handle positioning
          const updated = [...oldData.data.sessions];
          updated[sessionIndex] = mergedSession;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              sessions: updated,
            },
          };
        });
      });

      // Update stats cache if we found the old session - use latest query key
      if (oldSession) {
        const statsQueries = queryClient
          .getQueryCache()
          .findAll(['sessions-stats', companyId, dataSourceId]);

        statsQueries.forEach(query => {
          queryClient.setQueryData(query.queryKey, (oldData: any) => {
            if (!oldData?.data) return oldData;

            const stats = oldData.data;
            const totalSessions = stats.total_sessions || 1;

            // Calculate the difference
            const pagesDiff = data.session.total_pages - oldSession!.total_pages;
            const durationDiff = data.session.duration_seconds - oldSession!.duration_seconds;

            return {
              ...oldData,
              data: {
                ...stats,
                // Update averages with the new data
                avg_pages_per_session:
                  (stats.avg_pages_per_session * totalSessions -
                    oldSession!.total_pages +
                    data.session.total_pages) /
                  totalSessions,
                avg_duration_seconds:
                  (stats.avg_duration_seconds * totalSessions -
                    oldSession!.duration_seconds +
                    data.session.duration_seconds) /
                  totalSessions,
              },
            };
          });
        });
      }

      // Refresh stats only (not sessions list)
      queryClient.invalidateQueries({ queryKey: ['sessions-stats'], exact: false });
    };

    socket.on('sessions:new', handleNewSession);
    socket.on('sessions:updated', handleSessionUpdate);

    // Listen for subscription confirmation
    socket.on('sessions:subscribed', (data: { room: string }) => {
      console.log('✅ Successfully subscribed to room:', data.room);
    });

    return () => {
      console.log('🔌 Unsubscribing from sessions room');
      socket.off('sessions:new', handleNewSession);
      socket.off('sessions:updated', handleSessionUpdate);
      socket.off('sessions:subscribed');
      socket.emit('sessions:unsubscribe', { companyId, dataSourceId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, connected, companyId, dataSourceId]);

  const getDeviceIcon = (device: string) => {
    const deviceLower = device?.toLowerCase() || '';
    if (
      deviceLower.includes('mobile') ||
      deviceLower.includes('iphone') ||
      deviceLower.includes('android')
    ) {
      return <Smartphone className="w-5 h-5 text-blue-500" />;
    }
    if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) {
      return <Tablet className="w-5 h-5 text-purple-500" />;
    }
    return <Monitor className="w-5 h-5 text-green-500" />;
  };

  // Handle table sort (CLIENT-SIDE for instant sorting)
  const handleTableSort = (
    column: 'session_id' | 'first_timestamp' | 'last_timestamp' | 'total_pages' | 'duration_seconds'
  ) => {
    if (clientSortBy === column) {
      setClientSortOrder(clientSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setClientSortBy(column);
      setClientSortOrder('desc');
    }
  };

  // Sortable header component (matching Funnel Analysis style)
  const SortableHeader = ({
    column,
    children,
    className = '',
  }: {
    column:
      | 'session_id'
      | 'first_timestamp'
      | 'last_timestamp'
      | 'total_pages'
      | 'duration_seconds';
    children: React.ReactNode;
    className?: string;
  }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors select-none ${className}`}
      onClick={() => handleTableSort(column)}
    >
      <div className="flex items-center gap-2">
        {children}
        {clientSortBy === column && (
          <ArrowUpDown
            className={`w-3 h-3 transition-transform ${
              clientSortOrder === 'desc' ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>
    </th>
  );

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const formatUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search;
    } catch {
      return url;
    }
  };

  // 🎨 Quality Badge Component - Color-coded warnings
  const QualityBadge = ({ quality }: { quality: SessionData['quality'] }) => {
    if (quality.hasHighEngagement) {
      return (
        <div className="flex items-center gap-1 text-green-600" title="High engagement session">
          <Star className="w-4 h-4 fill-current" />
        </div>
      );
    }

    if (quality.hasLowEngagement) {
      return (
        <div className="flex items-center gap-1 text-red-600" title="Low engagement - Quick exit">
          <XCircle className="w-4 h-4" />
        </div>
      );
    }

    if (quality.isBounce) {
      return (
        <div className="flex items-center gap-1 text-yellow-600" title="Bounce - Single page view">
          <AlertTriangle className="w-4 h-4" />
        </div>
      );
    }

    if (quality.isShortSession) {
      return (
        <div className="flex items-center gap-1 text-orange-600" title="Quick session (<10s)">
          <Clock className="w-4 h-4" />
        </div>
      );
    }

    if (quality.isLongSession) {
      return (
        <div className="flex items-center gap-1 text-blue-600" title="Long session (30+ min)">
          <Zap className="w-4 h-4" />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 text-gray-400" title="Normal session">
        <span className="w-4 h-4 text-center text-xs">✓</span>
      </div>
    );
  };

  const handleRefresh = async () => {
    // Save the current live-updated sessions before refetch
    const liveUpdatedSessions = new Map<string, SessionData>();

    // Collect all sessions that have been updated via WebSocket
    const queries = queryClient.getQueryCache().findAll(['sessions', companyId, dataSourceId]);
    queries.forEach(query => {
      const data = query.state.data as { data: SessionsResponse } | undefined;
      data?.data?.sessions.forEach(session => {
        liveUpdatedSessions.set(session.session_id, session);
      });
    });

    // Also include newSessions
    newSessions.forEach(session => {
      liveUpdatedSessions.set(session.session_id, session);
    });

    // Refetch from API
    await refetch();

    // Re-apply WebSocket updates to the fresh data
    // This ensures we keep the latest page counts, durations, etc.
    const allQueries = queryClient.getQueryCache().findAll(['sessions', companyId, dataSourceId]);
    allQueries.forEach(query => {
      queryClient.setQueryData<{ data: SessionsResponse }>(query.queryKey, oldData => {
        if (!oldData?.data?.sessions) return oldData;

        return {
          ...oldData,
          data: {
            ...oldData.data,
            sessions: oldData.data.sessions.map(s => {
              // If we have a live-updated version, use that instead
              const liveVersion = liveUpdatedSessions.get(s.session_id);
              if (
                liveVersion &&
                new Date(liveVersion.last_timestamp) >= new Date(s.last_timestamp)
              ) {
                return liveVersion;
              }
              return s;
            }),
          },
        };
      });
    });

    await queryClient.invalidateQueries({ queryKey: ['sessions-stats'], exact: false });
    toast.success('Sessions refreshed!');
  };

  const handleViewJourney = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setShowJourneyModal(true);
  };

  const handleCloseJourney = () => {
    setShowJourneyModal(false);
    setSelectedSessionId(null);
  };

  const handleClearAllFilters = () => {
    setDeviceFilter('all');
    setUtmSourceFilter('');
    setUtmCampaignFilter('');
    setUtmMediumFilter('');
    setCurrentPageFilter({ operator: 'contains', value: '' });
    setDateRange(getDefaultDateRange());
    toast.success('All filters cleared');
  };

  // Reset page when filters change (but NOT on client sort change)
  useEffect(() => {
    setPage(1);
    setNewSessions([]);
  }, [
    dateRange,
    deviceFilter,
    utmSourceFilter,
    utmCampaignFilter,
    utmMediumFilter,
    currentPageFilter,
  ]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-secondary-900 dark:text-secondary-50">
            User Sessions
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            {selectedCompany?.name} • {selectedDataSource?.name}
            {newSessions.length > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-blue-600">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                {newSessions.length} new
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card animate-slide-down">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
            Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Device Type */}
            <div>
              <label className="label">Device Type</label>
              <select
                value={deviceFilter}
                onChange={e => setDeviceFilter(e.target.value)}
                className="input"
              >
                <option value="all">All Devices</option>
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
                <option value="tablet">Tablet</option>
              </select>
            </div>

            {/* UTM Source Autocomplete */}
            <div>
              <label className="label">UTM Source</label>
              <select
                value={utmSourceFilter}
                onChange={e => {
                  setUtmSourceFilter(e.target.value);
                  setUtmCampaignFilter(''); // Reset cascading
                  setUtmMediumFilter('');
                }}
                className="input"
              >
                <option value="">All Sources</option>
                {(utmSources || []).map((item: any, idx: number) => (
                  <option key={idx} value={item.source}>
                    {item.source} ({item.count})
                  </option>
                ))}
              </select>
            </div>

            {/* UTM Campaign Autocomplete (Cascading) */}
            <div>
              <label className="label">UTM Campaign</label>
              <select
                value={utmCampaignFilter}
                onChange={e => setUtmCampaignFilter(e.target.value)}
                className="input"
                disabled={!utmSourceFilter}
              >
                <option value="">All Campaigns</option>
                {(utmCampaigns || []).map((item: any, idx: number) => (
                  <option key={idx} value={item.campaign}>
                    {item.campaign} ({item.count})
                  </option>
                ))}
              </select>
            </div>

            {/* UTM Medium Autocomplete (Cascading) */}
            <div>
              <label className="label">UTM Medium</label>
              <select
                value={utmMediumFilter}
                onChange={e => setUtmMediumFilter(e.target.value)}
                className="input"
                disabled={!utmSourceFilter}
              >
                <option value="">All Mediums</option>
                {(utmMediums || []).map((item: any, idx: number) => (
                  <option key={idx} value={item.medium}>
                    {item.medium} ({item.count})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Current Page Filter with Operator */}
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-50 mb-3">
              Current Page Filter
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Operator</label>
                <select
                  value={currentPageFilter.operator}
                  onChange={e =>
                    setCurrentPageFilter({
                      ...currentPageFilter,
                      operator: e.target.value as any,
                    })
                  }
                  className="input"
                >
                  <option value="contains">Contains</option>
                  <option value="equals">Equals</option>
                  <option value="startsWith">Starts With</option>
                  <option value="endsWith">Ends With</option>
                  <option value="notContains">Does Not Contain</option>
                </select>
              </div>
              <div className="relative">
                <label className="label">Page URL or Title</label>
                <input
                  type="text"
                  value={currentPageFilter.value}
                  onChange={e => {
                    setCurrentPageFilter({
                      ...currentPageFilter,
                      value: e.target.value,
                    });
                    setCurrentPageSearch(e.target.value);
                    setShowCurrentPageDropdown(true); // Always show when typing
                  }}
                  onFocus={() => setShowCurrentPageDropdown(true)} // Show all pages on focus
                  onBlur={() => setTimeout(() => setShowCurrentPageDropdown(false), 200)}
                  placeholder="e.g., /checkout or Checkout"
                  className="input"
                />
                {/* Autocomplete Dropdown */}
                {showCurrentPageDropdown && availablePages && availablePages.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {availablePages
                      .filter((p: any) => {
                        if (!currentPageSearch) return true; // Show all if no search
                        const searchLower = currentPageSearch.toLowerCase();
                        return (
                          p.page?.toLowerCase().includes(searchLower) ||
                          p.title?.toLowerCase().includes(searchLower)
                        );
                      })
                      .slice(0, 50) // Limit to 50 results for performance
                      .map((p: any, idx: number) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setCurrentPageFilter({
                              ...currentPageFilter,
                              value: p.page,
                            });
                            setShowCurrentPageDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
                        >
                          <div className="text-sm font-medium text-secondary-900 dark:text-secondary-50">
                            {p.title}
                          </div>
                          <div className="text-xs text-secondary-600 dark:text-secondary-400 truncate">
                            {p.page} • {p.count} sessions
                          </div>
                        </button>
                      ))}
                    {availablePages.filter((p: any) => {
                      if (!currentPageSearch) return false;
                      const searchLower = currentPageSearch.toLowerCase();
                      return (
                        p.page?.toLowerCase().includes(searchLower) ||
                        p.title?.toLowerCase().includes(searchLower)
                      );
                    }).length === 0 &&
                      currentPageSearch && (
                        <div className="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400 text-center">
                          No pages found matching "{currentPageSearch}"
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(deviceFilter !== 'all' ||
            utmSourceFilter ||
            utmCampaignFilter ||
            utmMediumFilter ||
            currentPageFilter.value ||
            dateRange.label !== 'Last 7 days') && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleClearAllFilters}
                className="btn btn-secondary text-sm flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stats Cards - Row 1: Core Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Total Sessions</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.total_sessions?.toLocaleString() || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Monitor className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Desktop</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.desktop_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Mobile</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.mobile_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Avg Pages/Session
              </p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.avg_pages_per_session?.toFixed(1) || '0.0'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
              <Clock className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Avg Duration</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.avg_duration_seconds
                    ? `${Math.floor(stats.avg_duration_seconds / 60)}m ${Math.floor(
                        stats.avg_duration_seconds % 60
                      )}s`
                    : '0m 0s'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Row 2: Engagement & Quality Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <LogOut className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Bounce Rate</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.bounce_rate}%
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">High Engagement</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.high_engagement_rate}%
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
              <Activity className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Active Now</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.active_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Paid Traffic</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.paid_traffic_rate}%
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Flame className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Power Users</p>
              {isLoading ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {stats.long_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-secondary-900 dark:text-secondary-50">
            Recent Sessions
          </h2>
          {newSessions.length > 0 && (
            <span className="badge badge-info animate-pulse">
              {newSessions.length} new session{newSessions.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className="h-24 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded-lg"
              />
            ))}
          </div>
        ) : sessions.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-8 px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                      Status
                    </th>
                    <SortableHeader column="session_id" className="w-32">
                      Session ID
                    </SortableHeader>
                    <SortableHeader column="first_timestamp">Started</SortableHeader>
                    <SortableHeader column="last_timestamp">Last Active</SortableHeader>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                      Device
                    </th>
                    <SortableHeader column="total_pages">Pages</SortableHeader>
                    <SortableHeader column="duration_seconds">Duration</SortableHeader>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase min-w-[200px]">
                      Current Page
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase min-w-[180px]">
                      User (Shop 1)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, index) => {
                    const isNew = newSessions.some(s => s.session_id === session.session_id);
                    return (
                      <tr
                        key={session.session_id}
                        className={isNew ? 'bg-blue-50 dark:bg-blue-900/20 animate-fade-in' : ''}
                      >
                        <td>
                          <QualityBadge quality={session.quality} />
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {isNew && (
                              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                            )}
                            <span className="font-mono text-xs" title={session.session_id}>
                              {session.session_id?.substring(0, 8)}...
                            </span>
                          </div>
                        </td>
                        <td className="text-sm whitespace-nowrap">
                          <div>
                            <div className="font-medium text-secondary-900 dark:text-secondary-50">
                              {new Date(session.first_timestamp).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-secondary-500">
                              {new Date(session.first_timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </td>
                        <td className="text-sm whitespace-nowrap">
                          <div>
                            <div className="font-medium text-secondary-900 dark:text-secondary-50">
                              {formatDistanceToNow(new Date(session.last_timestamp), {
                                addSuffix: true,
                              })}
                            </div>
                            <div className="text-xs text-secondary-500">
                              {new Date(session.last_timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {getDeviceIcon(session.device_type)}
                            <span className="capitalize text-sm">
                              {session.device_type?.split(' ')[0] || 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-info">{session.total_pages}</span>
                        </td>
                        <td className="text-sm">{formatDuration(session.duration_seconds)}</td>
                        <td className="max-w-xs">
                          <div className="truncate">
                            <p className="text-sm font-medium text-secondary-900 dark:text-secondary-50 truncate">
                              {session.last_page_title || 'Untitled'}
                            </p>
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 truncate">
                              {formatUrl(session.last_page)}
                            </p>
                          </div>
                        </td>
                        <td className="max-w-xs">
                          {session.userInfo ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Users className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  {session.userInfo.name ? (
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-50 truncate">
                                        {session.userInfo.name}
                                      </p>
                                      <a
                                        href={`https://quickmeds.co.uk/dash/users/${session.userInfo.userId}/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-shrink-0 w-4 h-4 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                                        title={`Open user ${session.userInfo.userId} in QuickMeds dashboard`}
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                      </a>
                                    </div>
                                  ) : session.userInfo.isUnknownUser ? (
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm text-amber-600 dark:text-amber-400 truncate">
                                        User ID {session.userInfo.userId}
                                      </p>
                                      <a
                                        href={`https://quickmeds.co.uk/dash/users/${session.userInfo.userId}/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-shrink-0 w-4 h-4 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 transition-colors"
                                        title={`Open user ${session.userInfo.userId} in QuickMeds dashboard`}
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                      </a>
                                    </div>
                                  ) : null}
                                  {session.userInfo.email && (
                                    <p className="text-xs text-secondary-600 dark:text-secondary-400 truncate">
                                      {session.userInfo.email}
                                    </p>
                                  )}
                                  {session.userInfo.phoneNumber && (
                                    <p className="text-xs text-secondary-600 dark:text-secondary-400 truncate">
                                      {session.userInfo.phoneNumber}
                                    </p>
                                  )}
                                  {session.userInfo.totalOrders !== undefined &&
                                    session.userInfo.totalOrders > 0 && (
                                      <p className="text-xs text-green-600 dark:text-green-400">
                                        {session.userInfo.totalOrders} orders
                                      </p>
                                    )}
                                  {session.userInfo.customerMetrics && (
                                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                          session.userInfo.customerMetrics.customerSegment === 'VIP'
                                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                            : session.userInfo.customerMetrics.customerSegment ===
                                              'At Risk'
                                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                            : session.userInfo.customerMetrics.customerSegment ===
                                              'Returning'
                                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                        }`}
                                      >
                                        {session.userInfo.customerMetrics.customerSegment}
                                      </span>
                                      <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                        £
                                        {session.userInfo.customerMetrics.totalSpent ||
                                          session.userInfo.customerMetrics.estimatedLifetimeValue}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {session.userInfo.multipleUsersDetected && (
                                <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                                  <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                                  <span>
                                    Multiple users ({session.userInfo.multipleUserIds?.length})
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-secondary-400 dark:text-secondary-600 italic">
                              No user detected
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <Globe className="w-3.5 h-3.5 text-secondary-500" />
                            <span className="capitalize text-sm">
                              {session.utm_source || 'Direct'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => handleViewJourney(session.session_id)}
                            className="btn btn-sm btn-primary flex items-center gap-1"
                            title="View session journey"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span className="hidden lg:inline">View</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-secondary-200 dark:border-secondary-700">
                <div className="text-sm text-secondary-600 dark:text-secondary-400">
                  Showing{' '}
                  <span className="font-medium text-secondary-900 dark:text-secondary-50">
                    {(page - 1) * limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium text-secondary-900 dark:text-secondary-50">
                    {Math.min(page * limit, totalSessions)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-secondary-900 dark:text-secondary-50">
                    {totalSessions}
                  </span>{' '}
                  sessions
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    title="First page"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-2">
                    {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (page <= 3) {
                        pageNum = idx + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = page - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            page === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                    className="px-3 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    title="Last page"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-secondary-400">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No sessions found</p>
            <p className="text-sm mt-2">Try adjusting your filters or date range</p>
          </div>
        )}
      </div>

      {/* Session Journey Modal */}
      {selectedSessionId && (
        <SessionJourneyModal
          isOpen={showJourneyModal}
          onClose={handleCloseJourney}
          sessionId={selectedSessionId}
          companyId={companyId!}
          dataSourceId={dataSourceId!}
        />
      )}
    </div>
  );
};

export default SessionsPage;
